import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Skeleton } from '../ui/Skeleton'
import { plural } from '../../utils/plural'
import { formatDateChapter as formatDate } from '../../utils/date'

export default function ChapterList({ chapters, volumes, mangaSlug, loading, completedIds = new Set() }) {
  const [sortDesc, setSortDesc] = useState(true)
  const [collapsed, setCollapsed] = useState({})
  const [showAllVolumes, setShowAllVolumes] = useState(false)

  const grouped = useMemo(() => [...(volumes || [])]
    .sort((a, b) => sortDesc ? b.number - a.number : a.number - b.number)
    .map((vol) => ({
      volume: vol,
      chapters: (chapters || [])
        .filter((ch) => ch.volume_id === vol.id)
        .sort((a, b) => sortDesc ? b.number - a.number : a.number - b.number),
    })), [volumes, chapters, sortDesc])

  if (loading) {
    return (
      <div className="flex flex-col gap-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 rounded-lg bg-surface animate-pulse">
            <Skeleton className="h-4 w-1/3 rounded" />
            <Skeleton className="h-3 w-1/5 rounded" />
          </div>
        ))}
      </div>
    )
  }

  if (!chapters?.length) {
    return <p className="py-8 text-center text-muted">Глав пока нет</p>
  }

  const showVolumeHeaders = volumes?.length > 1

  const flatSorted = grouped.flatMap((g) => g.chapters)

  const toggleVolume = (id, current) => setCollapsed((prev) => ({ ...prev, [id]: !current }))

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-muted">
          {chapters.length} {plural(chapters.length, ['глава', 'главы', 'глав'])}
        </span>
        <button
          onClick={() => setSortDesc((v) => !v)}
          className="flex items-center gap-1 text-sm text-muted hover:text-fg transition-colors px-2 py-1 rounded"
        >
          {sortDesc ? '↓ Новые' : '↑ Старые'}
        </button>
      </div>

      <div className="flex flex-col gap-1">
        {showVolumeHeaders
          ? (() => {
              const EDGE = 3
              const needCollapse = !showAllVolumes && grouped.length > EDGE * 2
              const visible = needCollapse
                ? [...grouped.slice(0, EDGE), null, ...grouped.slice(-EDGE)]
                : grouped
              const hiddenStart = grouped[EDGE]?.volume.number
              const hiddenEnd = grouped[grouped.length - EDGE - 1]?.volume.number

              return visible.map((item, i) => {
                if (item === null) return (
                  <button key="separator" onClick={() => setShowAllVolumes(true)}
                    className="w-full flex items-center justify-center gap-2 py-2 text-xs font-bold uppercase tracking-wider text-accent hover:opacity-70 transition-opacity rounded-lg cursor-pointer">
                    <span className="tracking-widest opacity-60">· · ·</span>
                    <span>Том {hiddenStart} – Том {hiddenEnd}</span>
                    <span className="tracking-widest opacity-60">· · ·</span>
                  </button>
                )

                const { volume, chapters: vChapters } = item
                if (!vChapters.length) return null
                const isCollapsed = collapsed[volume.id] ?? (volume.id !== grouped[0]?.volume.id)
                const readCount = vChapters.filter((ch) => completedIds.has(ch.id)).length
              return (
                <div key={volume.id} className="mb-1">
                  <button
                    onClick={() => toggleVolume(volume.id, isCollapsed)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-accent hover:bg-surface2 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <svg
                        width="18" height="18" viewBox="0 0 16 16" fill="none"
                        className="shrink-0 transition-transform duration-200"
                        style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                      >
                        <path d="M3 5.5L8 10.5L13 5.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Том {volume.number}{volume.title ? ` — ${volume.title}` : ''}
                    </span>
                    <span className="flex items-center gap-2 font-normal normal-case tracking-normal text-muted">
                      {readCount > 0 && <span className="text-accent">{readCount}/{vChapters.length}</span>}
                      <span>{vChapters.length} {plural(vChapters.length, ['гл.', 'гл.', 'гл.'])}</span>
                    </span>
                  </button>
                  {!isCollapsed && (
                    <div className="flex flex-col border-l-2 border-line ml-4">
                      {vChapters.map((ch, i) => (
                        <div key={ch.id} className={i < vChapters.length - 1 ? 'border-b border-line' : ''}>
                          <ChapterRow chapter={ch} mangaSlug={mangaSlug} completedIds={completedIds} indent />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
            })()
          : flatSorted.map((ch) => (
              <ChapterRow key={ch.id} chapter={ch} mangaSlug={mangaSlug} completedIds={completedIds} />
            ))}
      </div>
    </div>
  )
}

function ChapterRow({ chapter: ch, mangaSlug, completedIds, indent = false }) {
  const isRead = completedIds.has(ch.id)

  return (
    <Link
      to={`/manga/${mangaSlug}/chapter/${ch.number}`}
      className={`group flex items-center justify-between py-3 transition-colors hover:bg-surface2 ${indent ? 'pl-5 pr-4' : 'px-4 rounded-lg bg-surface'}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isRead && <span className="text-xs text-accent shrink-0">✓</span>}
        <span className={`text-sm font-medium truncate ${isRead ? 'text-muted' : 'text-fg'}`}>
          Глава {ch.number}{ch.title ? ` — ${ch.title}` : ''}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        <span className="text-xs text-muted">{formatDate(ch.created_at)}</span>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-line text-muted group-hover:text-accent group-hover:border-accent transition-colors">
          Читать
        </span>
      </div>
    </Link>
  )
}
